import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { GraduationCap, LogOut, Star } from 'lucide-react';
import { supabase } from '../database/supabase';

const TeacherEvaluationPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [credentials, setCredentials] = useState({ studentId: '', password: '' });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing student session
    const savedStudent = localStorage.getItem('student_session');
    if (savedStudent) {
      try {
        const student = JSON.parse(savedStudent);
        setStudentData(student);
        setIsLoggedIn(true);
      } catch (err) {
        localStorage.removeItem('student_session');
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('student_accounts')
        .select('*')
        .eq('student_id', credentials.studentId)
        .eq('status', 'approved')
        .single();

      if (error || !data) {
        throw new Error('Invalid credentials or account not approved');
      }

      // In production, verify password hash
      setStudentData(data);
      setIsLoggedIn(true);
      localStorage.setItem('student_session', JSON.stringify(data));
      toast.success('Login successful!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setStudentData(null);
    setCredentials({ studentId: '', password: '' });
    localStorage.removeItem('student_session');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-8 h-8 text-blue-800" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Teacher Evaluation</h1>
            <p className="text-gray-600 mt-2">Student Login</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="form-label">Student ID</label>
              <input
                type="text"
                className="form-input"
                value={credentials.studentId}
                onChange={(e) => setCredentials({...credentials, studentId: e.target.value})}
                required
              />
            </div>

            <div>
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-input"
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                onClick={() => navigate('/evaluation-register')}
                className="text-blue-800 hover:text-red-900 font-medium"
              >
                Request Access
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Teacher Evaluation Portal</h1>
              <p className="text-sm text-gray-600">Welcome, {studentData.name}</p>
            </div>
            <button
              onClick={handleLogout}
              className="btn-outline flex items-center"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-custom py-8">
        <EvaluationDashboard studentId={studentData.id} />
      </main>
    </div>
  );
};

const EvaluationDashboard = ({ studentId }) => {
  const [enrolledSubjects, setEnrolledSubjects] = useState([]);
  const [availableTeacherSubjects, setAvailableTeacherSubjects] = useState([]);
  const [submittedEvaluations, setSubmittedEvaluations] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [studentId]);

  const loadData = async () => {
    try {
      // First get student's campus, department, and level
      const { data: studentInfo, error: studentError } = await supabase
        .from('student_accounts')
        .select('campus, department, level')
        .eq('id', studentId)
        .maybeSingle();

      if (studentError) {
        console.error('Student info error:', studentError);
        throw studentError;
      }

      const studentCampus = studentInfo?.campus || '';
      const studentDepartment = studentInfo?.department || '';
      const studentLevel = studentInfo?.level || '';

      // Load enrolled subjects
      const { data: enrolled, error: enrolledError } = await supabase
        .from('student_subjects')
        .select(`
          *,
          subjects(name, code),
          teachers(name)
        `)
        .eq('student_id', studentId);

      if (enrolledError) {
        console.error('Enrolled error:', enrolledError);
        throw enrolledError;
      }
      setEnrolledSubjects(enrolled || []);

      // Load submitted evaluations
      const { data: submitted, error: submittedError } = await supabase
        .from('teacher_evaluations')
        .select('teacher_id, subject_id, academic_year, semester')
        .eq('student_id', studentId);

      if (submittedError) {
        console.error('Submitted error:', submittedError);
        throw submittedError;
      }
      setSubmittedEvaluations(submitted || []);

      console.log('Student filters - Campus:', studentCampus, 'Department:', studentDepartment, 'Level:', studentLevel);

      // Load available teacher-subject combinations (filter by student campus, department, and level)
      const { data: available, error: availableError } = await supabase
        .from('teacher_subjects')
        .select(`
          *,
          teachers(id, name, is_active),
          subjects(id, code, name)
        `)
        .eq('campus', studentCampus)
        .eq('department', studentDepartment)
        .eq('level', studentLevel);

      if (availableError) {
        console.error('Available error:', availableError);
        throw availableError;
      }
      
      console.log('Available teacher-subjects:', available);
      console.log('Number of matches:', available?.length || 0);
      
      // Filter only active teachers
      const activeTeacherSubjects = (available || []).filter(ts => ts.teachers?.is_active);
      console.log('Active teacher-subjects:', activeTeacherSubjects);
      console.log('Number of active:', activeTeacherSubjects.length);
      setAvailableTeacherSubjects(activeTeacherSubjects);
    } catch (err) {
      console.error('Load data error:', err);
      toast.error('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const enrollInSubject = async (teacherSubjectId, teacherId, subjectId, academicYear, semester) => {
    try {
      const { error } = await supabase
        .from('student_subjects')
        .insert([{
          student_id: studentId,
          subject_id: subjectId,
          teacher_id: teacherId,
          academic_year: academicYear,
          semester: semester
        }]);

      if (error) throw error;
      toast.success('Successfully enrolled!');
      setShowAddModal(false);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to enroll');
    }
  };

  const removeSubject = async (subjectEnrollmentId) => {
    if (!window.confirm('Remove this subject from your list?')) return;
    
    try {
      const { error } = await supabase
        .from('student_subjects')
        .delete()
        .eq('id', subjectEnrollmentId);

      if (error) throw error;
      toast.success('Subject removed');
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to remove subject');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Your Subjects</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
        >
          + Add Subject to Evaluate
        </button>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {enrolledSubjects.map((subject) => {
          const isSubmitted = submittedEvaluations.some(
            ev => ev.teacher_id === subject.teacher_id && 
                  ev.subject_id === subject.subject_id &&
                  ev.academic_year === subject.academic_year &&
                  ev.semester === subject.semester
          );
          
          return (
          <div key={subject.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                {subject.subjects?.name}
              </h3>
              <button
                onClick={() => removeSubject(subject.id)}
                className="text-red-600 hover:text-blue-800 text-sm"
                title="Remove subject"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-1">Code: {subject.subjects?.code}</p>
            <p className="text-sm text-gray-600 mb-4">Teacher: {subject.teachers?.name}</p>
            
            {isSubmitted ? (
              <div className="w-full py-2 px-4 bg-green-100 text-green-800 rounded-lg text-center font-medium">
                ✓ Submitted
              </div>
            ) : (
              <button 
                className="btn-primary w-full flex items-center justify-center"
                onClick={() => { setSelectedSubject(subject); setShowEvalModal(true); }}
              >
                <Star className="w-4 h-4 mr-2" />
                Evaluate Teacher
              </button>
            )}
          </div>
          );
        })}
      </div>

      {enrolledSubjects.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">No subjects enrolled yet.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-outline"
          >
            Add Your First Subject
          </button>
        </div>
      )}

      {showAddModal && (
        <AddSubjectModal
          studentId={studentId}
          availableTeacherSubjects={availableTeacherSubjects}
          enrolledSubjects={enrolledSubjects}
          onEnroll={enrollInSubject}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {showEvalModal && selectedSubject && (
        <EvaluationFormModal
          studentId={studentId}
          enrollment={selectedSubject}
          onClose={() => { setShowEvalModal(false); setSelectedSubject(null); }}
          onSubmit={() => { setShowEvalModal(false); setSelectedSubject(null); loadData(); }}
        />
      )}
    </div>
  );
};

const AddSubjectModal = ({ studentId, availableTeacherSubjects, enrolledSubjects, onEnroll, onClose }) => {
  const [selectedTeacherSubject, setSelectedTeacherSubject] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const [semester, setSemester] = useState('');
  const [studentDepartment, setStudentDepartment] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);
  const [availableSemesters, setAvailableSemesters] = useState([]);
  const [filteredTeacherSubjects, setFilteredTeacherSubjects] = useState([]);

  useEffect(() => {
    const loadStudentDepartment = async () => {
      const { data } = await supabase
        .from('student_accounts')
        .select('department')
        .eq('id', studentId)
        .maybeSingle();
      setStudentDepartment(data?.department || '');
    };
    loadStudentDepartment();
    
    // Get unique academic years from available teacher subjects
    const uniqueYears = [...new Set(availableTeacherSubjects.map(ts => ts.academic_year))].filter(Boolean).sort().reverse();
    setAvailableAcademicYears(uniqueYears);
  }, [studentId, availableTeacherSubjects]);

  // When academic year changes, update available semesters
  useEffect(() => {
    if (academicYear) {
      const semestersForYear = [...new Set(
        availableTeacherSubjects
          .filter(ts => ts.academic_year === academicYear)
          .map(ts => ts.semester)
      )].filter(Boolean);
      setAvailableSemesters(semestersForYear);
      
      // Auto-select first semester if only one available
      if (semestersForYear.length === 1) {
        setSemester(semestersForYear[0]);
      } else {
        setSemester('');
      }
    } else {
      setAvailableSemesters([]);
      setSemester('');
    }
  }, [academicYear, availableTeacherSubjects]);

  // Filter teacher subjects based on academic year and semester
  useEffect(() => {
    if (academicYear && semester) {
      const filtered = availableTeacherSubjects.filter(ts => 
        ts.academic_year === academicYear && ts.semester === semester
      );
      setFilteredTeacherSubjects(filtered);
    } else {
      setFilteredTeacherSubjects([]);
    }
    // Reset selection when filters change
    setSelectedTeacherSubject('');
    setSearchTerm('');
  }, [academicYear, semester, availableTeacherSubjects]);

  // Filter out already enrolled combinations
  const enrolledKeys = enrolledSubjects.map(es => `${es.teacher_id}_${es.subject_id}_${es.academic_year}_${es.semester}`);
  const availableOptions = filteredTeacherSubjects.filter(ts => 
    !enrolledKeys.includes(`${ts.teacher_id}_${ts.subject_id}_${ts.academic_year}_${ts.semester}`)
  );

  // Filter by search term
  const filteredOptions = availableOptions.filter(ts => {
    const searchLower = searchTerm.toLowerCase();
    const subjectName = ts.subjects?.name?.toLowerCase() || '';
    const teacherName = ts.teachers?.name?.toLowerCase() || '';
    const subjectCode = ts.subjects?.code?.toLowerCase() || '';
    return subjectName.includes(searchLower) || teacherName.includes(searchLower) || subjectCode.includes(searchLower);
  });

  const getDisplayText = (ts) => {
    return `${ts.subjects?.code} - ${ts.subjects?.name} (${ts.teachers?.name})`;
  };

  const handleSelect = (ts) => {
    setSelectedTeacherSubject(`${ts.id}|${ts.teacher_id}|${ts.subject_id}`);
    setSearchTerm(getDisplayText(ts));
    setShowDropdown(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedTeacherSubject) return;

    const [teacherSubjectId, teacherId, subjectId] = selectedTeacherSubject.split('|');
    onEnroll(teacherSubjectId, teacherId, subjectId, academicYear, semester);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Add Subject to Evaluate</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label">Academic Year *</label>
            <select
              className="form-input"
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              required
            >
              <option value="">Select Academic Year</option>
              {availableAcademicYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            {availableAcademicYears.length === 0 && (
              <p className="text-sm text-gray-500 mt-1">No academic years available</p>
            )}
          </div>

          <div>
            <label className="form-label">Semester/Quarter *</label>
            <select
              className="form-input"
              value={semester}
              onChange={(e) => setSemester(e.target.value)}
              disabled={!academicYear}
              required
            >
              <option value="">Select Semester/Quarter</option>
              {availableSemesters.map(sem => (
                <option key={sem} value={sem}>{sem}</option>
              ))}
            </select>
            {!academicYear && (
              <p className="text-sm text-gray-500 mt-1">Select academic year first</p>
            )}
            {academicYear && availableSemesters.length === 0 && (
              <p className="text-sm text-gray-500 mt-1">No semesters available for selected year</p>
            )}
          </div>

          <div>
            <label className="form-label">Select Teacher & Subject *</label>
            <div className="relative">
              <input
                type="text"
                className="form-input w-full pr-10"
                placeholder={academicYear && semester ? "Search or click arrow to see subjects..." : "Select academic year and semester first"}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowDropdown(true);
                  if (!e.target.value) setSelectedTeacherSubject('');
                }}
                onFocus={() => setShowDropdown(true)}
                disabled={!academicYear || !semester}
                required
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                onClick={() => setShowDropdown(!showDropdown)}
                disabled={!academicYear || !semester}
              >
                ▼
              </button>
              {showDropdown && filteredOptions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredOptions.map((ts) => (
                    <div
                      key={ts.id}
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onClick={() => handleSelect(ts)}
                    >
                      <div className="font-medium text-gray-900">{ts.subjects?.code} - {ts.subjects?.name}</div>
                      <div className="text-sm text-gray-600">Teacher: {ts.teachers?.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {academicYear && semester && availableOptions.length === 0 && (
              <p className="text-sm text-gray-500 mt-1">All available subjects are already enrolled or no subjects available</p>
            )}
            {searchTerm && filteredOptions.length === 0 && availableOptions.length > 0 && (
              <p className="text-sm text-gray-500 mt-1">No subjects found matching "{searchTerm}"</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button type="submit" className="btn-primary flex-1">
              Add Subject
            </button>
            <button type="button" onClick={onClose} className="btn-outline flex-1">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeacherEvaluationPage;

const EvaluationFormModal = ({ studentId, enrollment, onClose, onSubmit }) => {
  const [form, setForm] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadForm();
  }, []);

  const loadForm = async () => {
    try {
      console.log('Loading form for enrollment:', enrollment);
      
      // Get student's campus, department, and level
      const { data: studentInfo, error: studentError } = await supabase
        .from('student_accounts')
        .select('campus, department, level')
        .eq('id', studentId)
        .maybeSingle();

      if (studentError) {
        console.error('Error fetching student info:', studentError);
        throw studentError;
      }

      console.log('Student info:', studentInfo);

      const { data: tsData, error: tsError } = await supabase
        .from('teacher_subjects')
        .select('form_id, campus, department, level')
        .eq('teacher_id', enrollment.teacher_id)
        .eq('subject_id', enrollment.subject_id)
        .eq('academic_year', enrollment.academic_year)
        .eq('semester', enrollment.semester)
        .eq('campus', studentInfo?.campus || '')
        .eq('department', studentInfo?.department || '')
        .eq('level', studentInfo?.level || '');

      console.log('Teacher-subject query result:', tsData, 'Error:', tsError);
      console.log('First result:', tsData?.[0]);
      console.log('Form ID from result:', tsData?.[0]?.form_id);
      
      let formId = tsData?.[0]?.form_id;
      
      if (!formId) {
        console.log('No specific form assigned, loading active form');
        const { data: activeForms } = await supabase
          .from('evaluation_forms')
          .select('*')
          .eq('is_active', true)
          .limit(1);
        console.log('Active forms:', activeForms);
        setForm(activeForms?.[0] || null);
      } else {
        console.log('Loading specific form:', formId);
        const { data: specificForm } = await supabase
          .from('evaluation_forms')
          .select('*')
          .eq('id', formId)
          .maybeSingle();
        console.log('Specific form:', specificForm);
        setForm(specificForm);
      }
    } catch (err) {
      console.error('Error loading form:', err);
      toast.error('Failed to load evaluation form');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('teacher_evaluations')
        .insert([{
          form_id: form.id,
          teacher_id: enrollment.teacher_id,
          subject_id: enrollment.subject_id,
          student_id: studentId,
          responses,
          academic_year: enrollment.academic_year,
          semester: enrollment.semester
        }]);

      if (error) throw error;
      toast.success('Evaluation submitted successfully!');
      onSubmit();
    } catch (err) {
      if (err.code === '23505') {
        toast.error('You have already submitted an evaluation for this teacher/subject');
      } else {
        toast.error(err.message || 'Failed to submit evaluation');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8">Loading form...</div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8">
          <p>No evaluation form available</p>
          <button onClick={onClose} className="btn-primary mt-4">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-bold">{form.title}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {enrollment.subjects?.name} - {enrollment.teachers?.name}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {form.fields?.sections?.map((section, sIdx) => {
            const scaleFields = section.fields?.filter(f => f.type === 'SCALE') || [];
            const otherFields = section.fields?.filter(f => f.type !== 'SCALE') || [];
            
            return (
              <div key={sIdx} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 shadow-sm border border-blue-100">
                <div className="mb-4 pb-3 border-b-2 border-blue-200">
                  <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                  {section.description && (
                    <p className="text-sm text-gray-700 mt-2 italic">{section.description}</p>
                  )}
                </div>

                {scaleFields.length > 0 && (
                  <div className="mb-6 overflow-x-auto bg-white rounded-lg shadow-sm">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-blue-100">
                          <th className="border border-gray-300 p-3 text-left font-semibold text-gray-900">Question</th>
                          <th className="border border-gray-300 p-3 text-center font-semibold text-gray-900" colSpan="5">
                            {section.scaleLegend || (
                              scaleFields[0].scaleMin && scaleFields[0].scaleMax ? (
                                <div className="flex justify-between px-4">
                                  <span className="text-sm text-gray-900">{scaleFields[0].scaleMin}</span>
                                  <span className="text-sm text-gray-900">{scaleFields[0].scaleMax}</span>
                                </div>
                              ) : 'Rating (1-5)'
                            )}
                          </th>
                        </tr>
                        <tr className="bg-blue-100">
                          <th className="border border-blue-200 p-2 bg-blue-50"></th>
                          {[1, 2, 3, 4, 5].map(val => (
                            <th key={val} className="border border-blue-200 p-2 text-center font-semibold text-gray-900 w-16">{val}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {scaleFields.map((field, fIdx) => {
                          const fieldKey = `section_${sIdx}_field_${section.fields.indexOf(field)}`;
                          return (
                            <tr key={fIdx} className="hover:bg-blue-50 transition-colors">
                              <td className="border border-blue-200 p-3 bg-white text-gray-900">
                                {field.label}
                                {field.required && <span className="text-red-600 ml-1">*</span>}
                              </td>
                              {[1, 2, 3, 4, 5].map(val => (
                                <td key={val} className="border border-blue-200 p-2 text-center bg-white">
                                  <input
                                    type="radio"
                                    name={fieldKey}
                                    value={val}
                                    required={field.required}
                                    checked={responses[fieldKey] == val}
                                    onChange={(e) => setResponses({...responses, [fieldKey]: e.target.value})}
                                    className="w-4 h-4 cursor-pointer"
                                  />
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {otherFields.map((field, fIdx) => {
                  const fieldKey = `section_${sIdx}_field_${section.fields.indexOf(field)}`;
                  return (
                    <div key={fIdx} className="mb-4 bg-white rounded-lg p-4 shadow-sm">
                      <label className="block font-semibold mb-2 text-gray-900">
                        {field.label}
                        {field.required && <span className="text-red-600 ml-1">*</span>}
                      </label>

                      {field.type === 'TEXT' && (
                        <input
                          type="text"
                          className="form-input w-full border-2 border-gray-300 rounded-lg p-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                          required={field.required}
                          value={responses[fieldKey] || ''}
                          onChange={(e) => setResponses({...responses, [fieldKey]: e.target.value})}
                        />
                      )}

                      {field.type === 'PARAGRAPH_TEXT' && (
                        <textarea
                          className="form-input w-full border-2 border-gray-300 rounded-lg p-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                          rows="4"
                          required={field.required}
                          value={responses[fieldKey] || ''}
                          onChange={(e) => setResponses({...responses, [fieldKey]: e.target.value})}
                        />
                      )}

                      {field.type === 'MULTIPLE_CHOICE' && (
                        <div className="space-y-2">
                          {field.options?.map((option, oIdx) => (
                            <label key={oIdx} className="flex items-center p-2 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors">
                              <input
                                type="radio"
                                name={fieldKey}
                                value={option}
                                required={field.required}
                                checked={responses[fieldKey] === option}
                                onChange={(e) => setResponses({...responses, [fieldKey]: e.target.value})}
                                className="mr-3 w-4 h-4 cursor-pointer"
                              />
                              <span className="text-gray-900">{option}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {field.type === 'CHECKBOX' && (
                        <div className="space-y-2">
                          {field.options?.map((option, oIdx) => (
                            <label key={oIdx} className="flex items-center p-2 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors">
                              <input
                                type="checkbox"
                                value={option}
                                checked={(responses[fieldKey] || []).includes(option)}
                                onChange={(e) => {
                                  const current = responses[fieldKey] || [];
                                  const updated = e.target.checked
                                    ? [...current, option]
                                    : current.filter(v => v !== option);
                                  setResponses({...responses, [fieldKey]: updated});
                                }}
                                className="mr-3 w-4 h-4 cursor-pointer"
                              />
                              <span className="text-gray-900">{option}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {field.type === 'DATE' && (
                        <input
                          type="date"
                          className="form-input w-full border-2 border-gray-300 rounded-lg p-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                          required={field.required}
                          value={responses[fieldKey] || ''}
                          onChange={(e) => setResponses({...responses, [fieldKey]: e.target.value})}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          <div className="flex gap-3 pt-4 border-t">
            <button type="submit" disabled={submitting} className="btn-primary flex-1">
              {submitting ? 'Submitting...' : 'Submit Evaluation'}
            </button>
            <button type="button" onClick={onClose} className="btn-outline flex-1">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
